/**
 * KrvPage – Krankenversicherung, Belegverwaltung (Wellen 94/95).
 *
 * Leitgedanke: DER ORDNER IST DER STATUS. Was in Dropbox unter „einzureichen"
 * liegt, steht hier in der Sektion Einzureichen. Wird der Status hier geändert,
 * wandert die Datei in Dropbox mit — und umgekehrt.
 *
 * Aufbau: Sektion → Datums-Bündel (aufklappbar) → Belege eingerückt darunter.
 * Welle 96: Klick auf eine Zeile zeigt das PDF rechts daneben (Pfeiltasten blättern),
 * Spalten sortierbar, Mahnung/Sonstiges/Steuer direkt aus dem Status-Auswahlfeld.
 * Auswahl wie in Excel: Shift+Pfeil / Shift-Klick markiert einen Bereich, Strg/Cmd-Klick
 * einzelne Zeilen; ein Statuswechsel an einer markierten Zeile gilt für alle markierten.
 *
 * Zeilen werden über einen SCHLÜSSEL „bereich|id|m" angesprochen, nicht über die ID:
 * eine Mahnung steht einmal eingerückt unter ihrer Rechnung und einmal in der Sektion
 * Mahnungen — mit der ID allein sprang die Pfeilnavigation zwischen beiden hin und her.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaFilePdf, FaSync, FaTrash, FaEuroSign, FaHourglassHalf, FaUnlink,
} from "react-icons/fa";
import { S, API, api, eur, datum, heute, IconKnopf, FilterKopf, BEREICH_NAME, BEREICHE_DIREKT,
         ERSTATTET_NAME, ERSTATTET_ZEICHEN } from "./krv/gemeinsam";
import AbrechnungSektion from "./krv/AbrechnungSektion";
import PdfVorschau from "./krv/PdfVorschau";
import GeminiWarnung from "../components/GeminiWarnung";

/* ── Dialog für den Statuswechsel ───────────────────────────────────────── */
function StatusDialog({ belege, ziel, onClose, onFertig }) {
  const beleg = belege[0];
  const [wert, setWert] = useState(heute());
  const [vorgang, setVorgang] = useState(beleg.vorgangsnummer || "");
  const [erstattung, setErstattung] = useState(beleg.erstattung || "");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const braucht = ziel === "eingereicht" || ziel === "bezahlt";

  const speichern = async () => {
    setBusy(true); setFehler("");
    try {
      for (const b of belege) {
        await api(`/belege/${b.id}/status`, "POST", {
          bereich: ziel,
          datum: braucht ? wert : null,
          vorgangsnummer: ziel === "bezahlt" ? (vorgang || null) : null,
          // Erstattung gilt nur für genau einen Beleg — bei Mehrfachauswahl nicht verteilen
          erstattung: ziel === "bezahlt" && belege.length === 1 && erstattung !== "" ? Number(erstattung) : null,
        });
      }
      onFertig();
    } catch (e) { setFehler(String(e.message || e)); setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex",
                  alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "var(--bg-card)", borderRadius: 10, padding: 20, width: 440, maxWidth: "92vw" }}
           onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px" }}>Status ändern</h3>
        <p style={{ fontSize: "0.85rem", color: "var(--color-gray-600)", margin: "0 0 14px" }}>
          {belege.length === 1 ? beleg.datei_name : `${belege.length} markierte Dateien`}
        </p>
        <p style={{ fontSize: "0.88rem", margin: "0 0 12px" }}>
          Neuer Status: <strong>{ziel === "nicht_zugeordnet" ? "Nicht zugeordnet"
            : ziel === "einzureichen" ? "Einzureichen" : ziel === "eingereicht" ? "Eingereicht" : "Bezahlt"}</strong>
          {braucht && <> — die Datei wandert in den Unterordner mit diesem Datum.</>}
        </p>
        {braucht && (
          <label style={{ display: "block", marginBottom: 10, fontSize: "0.82rem", fontWeight: 600 }}>
            {ziel === "eingereicht" ? "Eingereicht am" : "Bezahlt am"}
            <input type="date" style={S.input} value={wert} onChange={(e) => setWert(e.target.value)} />
          </label>
        )}
        {ziel === "bezahlt" && belege.length === 1 && (
          <>
            <label style={{ display: "block", marginBottom: 10, fontSize: "0.82rem", fontWeight: 600 }}>
              Vorgangsnummer Signal Iduna (optional)
              <input style={S.input} value={vorgang} onChange={(e) => setVorgang(e.target.value)} />
            </label>
            <label style={{ display: "block", marginBottom: 10, fontSize: "0.82rem", fontWeight: 600 }}>
              Erstatteter Betrag (optional)
              <input style={S.input} type="number" step="0.01" value={erstattung}
                     onChange={(e) => setErstattung(e.target.value)} />
            </label>
          </>
        )}
        {fehler && <div style={S.err}>{fehler}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button style={S.btn} onClick={onClose} disabled={busy}>Abbrechen</button>
          <button style={S.btnPrim} onClick={speichern} disabled={busy}>
            {busy ? "Verschiebe…" : "Verschieben"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Löschen mit Rückfrage ──────────────────────────────────────────────── */
function LoeschDialog({ beleg, onClose, onFertig }) {
  const [auchDatei, setAuchDatei] = useState(false);   // false = Eintrag löschen + PDF ins Archiv
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const anhaenge = (beleg.mahnungen || []).length;

  const loeschen = async () => {
    setBusy(true); setFehler("");
    try {
      await api(`/belege/${beleg.id}${auchDatei ? "?datei=1" : "?archiv=1"}`, "DELETE");
      onFertig();
    } catch (e) { setFehler(String(e.message || e)); setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px" }}>Wirklich löschen?</h3>
        <p style={{ fontSize: "0.85rem", color: "var(--color-gray-600)", margin: "0 0 14px" }}>
          {beleg.datei_name}
        </p>
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8, fontSize: "0.85rem" }}>
          <input type="radio" checked={!auchDatei} onChange={() => setAuchDatei(false)} />
          <span><strong>Eintrag löschen, PDF archivieren</strong> — die Datei wandert nach
            „KRV/Sonstiges/Archiv" und taucht beim Abgleich nicht wieder auf.</span>
        </label>
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 12, fontSize: "0.85rem" }}>
          <input type="radio" checked={auchDatei} onChange={() => setAuchDatei(true)} />
          <span><strong>Eintrag und PDF</strong> — die Datei landet im Dropbox-Papierkorb
            {beleg.ueberweisen_pfad ? " (die Kopie in Überweisen ebenfalls)" : ""}.</span>
        </label>
        {anhaenge > 0 && (
          <div style={S.info}>
            {anhaenge} angehängte {anhaenge === 1 ? "Mahnung bleibt" : "Mahnungen bleiben"} erhalten.
          </div>
        )}
        {fehler && <div style={S.err}>{fehler}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button style={S.btn} onClick={onClose} disabled={busy}>Abbrechen</button>
          <button style={S.btnRot} onClick={loeschen} disabled={busy}>
            {busy ? "Lösche…" : auchDatei ? "Eintrag und PDF löschen" : "Löschen und archivieren"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Eine Belegzeile + aufklappbare Details ─────────────────────────────── */
function BelegZeile({ beleg, sektion, onStatus, onGeaendert, bereiche, aktiv, markiert, onWaehlen, onMeldung,
                      onGeloescht, alsMahnung = false, mitErstattet = false }) {
  const spalten = mitErstattet ? 8 : 7;
  const zeile = `${sektion}|${beleg.id}|${alsMahnung ? "m" : ""}`;
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loeschen, setLoeschen] = useState(false);
  const [kopiert, setKopiert] = useState(false);

  const feldSpeichern = async (feld, wert) => {
    if ((beleg[feld] ?? "") === wert) return;
    try { await api(`/belege/${beleg.id}`, "PUT", { felder: { [feld]: wert } }); onGeaendert(); }
    catch (e) { alert("Speichern fehlgeschlagen: " + e.message); }
  };

  const analysieren = async () => {
    setBusy(true);
    try { await api(`/belege/${beleg.id}/analysieren?erzwinge=1`, "POST"); onGeaendert(); }
    catch (e) { alert("Analyse fehlgeschlagen: " + e.message); }
    setBusy(false);
  };

  const Feld = ({ feld, label, typ = "text", breite = "1 1 180px" }) => (
    <label style={{ flex: breite, fontSize: "0.78rem", fontWeight: 600, color: "var(--color-gray-600)" }}>
      {label}
      <input style={S.input} type={typ} defaultValue={beleg[feld] ?? ""}
             onBlur={(e) => feldSpeichern(feld, e.target.value)} />
    </label>
  );

  // Liegt der Beleg in einem Bereich außerhalb des Ablaufs (z. B. im Mahnungen-Ordner),
  // muss dieser trotzdem im Auswahlfeld stehen — sonst zeigt es den falschen Status.
  const statusOptionen = useMemo(() => {
    const dabei = bereiche.some((b) => b.bereich === beleg.bereich);
    return dabei ? bereiche
      : [{ bereich: beleg.bereich, name: BEREICH_NAME[beleg.bereich] || beleg.bereich }, ...bereiche];
  }, [bereiche, beleg.bereich]);

  // Welle 96: Überweisen-Kopie nur noch auf Knopfdruck
  const ueberweisen = async () => {
    if (beleg.ueberweisen_pfad) return;
    setKopiert(true);
    try {
      await api(`/belege/${beleg.id}/ueberweisen`, "POST");
      onMeldung && onMeldung(`Kopie in „Überweisen" angelegt: >${beleg.datei_name}`);
      onGeaendert();
    } catch (e) { alert("Kopie fehlgeschlagen: " + e.message); setKopiert(false); }
  };

  const mahnungLoesen = async () => {
    try { await api(`/belege/${beleg.id}/mahnung-loesen`, "POST"); onGeaendert(); }
    catch (e) { alert("Lösen fehlgeschlagen: " + e.message); }
  };

  return (
    <>
      <tr data-krv-zeile={zeile}
          style={{ cursor: "pointer", ...(alsMahnung ? S.zeileMahnung : null),
                   ...(markiert.has(zeile) ? S.zeileMarkiert : null),
                   ...(aktiv === zeile ? S.zeileGewaehlt : null) }}
          onClick={(e) => onWaehlen(zeile, e)}>
        <td style={{ ...S.td, paddingLeft: alsMahnung ? 30 : 10, whiteSpace: "nowrap" }}>
          <span style={S.aufklapper} title={offen ? "Details zuklappen" : "Details aufklappen"}
                onClick={(e) => { e.stopPropagation(); setOffen((o) => !o); }}>
            {offen ? "▾" : "▸"}
          </span>
          {alsMahnung && <span title="Mahnung zu diesem Beleg">↳ </span>}
          {datum(beleg.datum_beleg)}
        </td>
        <td style={S.td}>
          {alsMahnung && <span style={S.pillMahnung}>Mahnung</span>}
          {beleg.arzt || "—"}
          {beleg.ist_kein_krv ? <span style={S.pillWarn} title="Gemini: kein KRV-Beleg">kein KRV</span> : null}
          {beleg.dublette_stufe ? (
            <span style={beleg.dublette_stufe === "sicher" ? S.pillWarn : S.pillTeilweise}
                  title={`Dublettenprüfung: ${DUBLETTEN_NAME[beleg.dublette_stufe] || beleg.dublette_stufe}`}>
              {beleg.dublette_stufe === "sicher" ? "doppelt" : "doppelt?"}
            </span>
          ) : null}
        </td>
        <td style={S.td}>{beleg.person || "—"}</td>
        <td style={{ ...S.td, ...S.num }}>{eur(beleg.betrag)}</td>
        {mitErstattet && (
          <td style={{ ...S.td, ...S.num }}>
            {alsMahnung ? "" : <ErstattetZelle beleg={beleg} />}
          </td>
        )}
        <td style={S.td}>{beleg.rechnungsnummer || "—"}</td>
        <td style={S.td} onClick={(e) => e.stopPropagation()}>
          {alsMahnung ? (
            <span style={{ fontSize: "0.78rem", color: "var(--color-gray-600)",
                           display: "inline-flex", alignItems: "center", gap: 2 }}>
              wird nicht eingereicht
              <IconKnopf farbe="grau" titel="Zuordnung zu diesem Beleg aufheben"
                         onClick={mahnungLoesen}>
                <FaUnlink />
              </IconKnopf>
            </span>
          ) : (
            <select style={{ ...S.input, width: "auto" }} value={beleg.bereich}
                    onChange={(e) => { e.target.blur(); onStatus(beleg, e.target.value, zeile); }}>
              {statusOptionen.map((b) => <option key={b.bereich} value={b.bereich}>{b.name}</option>)}
            </select>
          )}
        </td>
        <td style={S.td} onClick={(e) => e.stopPropagation()}>
         <div style={S.iconGruppe}>
          <IconKnopf farbe="blau" titel="PDF rechts anzeigen" onClick={() => onWaehlen(zeile, {})}>
            <FaFilePdf />
          </IconKnopf>
          <IconKnopf farbe="orange" disabled={busy}
                     titel={beleg.gemini_am ? "Nochmal mit Gemini auswerten" : "Mit Gemini auswerten"}
                     onClick={analysieren}>
            <FaSync style={busy ? { animation: "spin 0.8s linear infinite" } : null} />
          </IconKnopf>
          <IconKnopf farbe="rot" titel="Löschen" onClick={() => setLoeschen(true)}>
            <FaTrash />
          </IconKnopf>
          {!beleg.gemini_am && (
            <IconKnopf farbe="grau" titel="Noch nicht ausgewertet" onClick={analysieren}>
              <FaHourglassHalf />
            </IconKnopf>
          )}
          {!alsMahnung && (
            beleg.ueberweisen_pfad ? (
              <IconKnopf farbe="gruen" titel="Kopie liegt schon im Ordner Überweisen">
                <FaEuroSign />
              </IconKnopf>
            ) : (
              <IconKnopf farbe="grau" disabled={kopiert}
                         titel="Kopie in den Ordner Überweisen legen" onClick={ueberweisen}>
                <FaEuroSign />
              </IconKnopf>
            )
          )}
         </div>
        </td>
      </tr>
      {offen && (
        <tr>
          <td colSpan={spalten} style={{ ...S.td, background: "var(--color-gray-100)" }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
              <Feld feld="datum_beleg" label="Belegdatum" typ="date" breite="0 0 150px" />
              <Feld feld="betrag" label="Betrag" typ="number" breite="0 0 120px" />
              <Feld feld="person" label="Versicherte Person" />
              <Feld feld="arzt" label="Behandler / Praxis" />
              <Feld feld="fachrichtung" label="Fachrichtung" breite="0 0 160px" />
              <Feld feld="rechnungsnummer" label="Rechnungsnummer" />
              <Feld feld="zahlungsempfaenger" label="Zahlungsempfänger" />
              <Feld feld="iban" label="IBAN" />
              <Feld feld="leistung" label="Leistung" breite="1 1 260px" />
              <Feld feld="eingereicht_am" label="Eingereicht am" typ="date" breite="0 0 150px" />
              <Feld feld="vorgangsnummer" label="Vorgangsnummer Signal Iduna" />
              <Feld feld="bezahlt_am" label="Bezahlt am" typ="date" breite="0 0 150px" />
              <Feld feld="erstattung" label="Erstattung" typ="number" breite="0 0 120px" />
              <Feld feld="selbstbehalt" label="Selbstbehalt" typ="number" breite="0 0 120px" />
              <Feld feld="notiz" label="Notiz" breite="1 1 100%" />
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: "0.78rem", color: "var(--color-gray-600)" }}>
              <span style={{ flex: 1, wordBreak: "break-all" }}>{beleg.datei_pfad}</span>
              <button style={S.btn} onClick={analysieren} disabled={busy}>
                {busy ? "Analysiere…" : beleg.gemini_am ? "Erneut auswerten" : "Mit Gemini auswerten"}
              </button>
            </div>
          </td>
        </tr>
      )}
      {/* Mahnungen hängen nur grafisch am Beleg — eigene Datei, eigener Datensatz */}
      {(beleg.mahnungen || []).map((m) => (
        <BelegZeile key={m.id} beleg={m} sektion={sektion} onStatus={onStatus} onGeaendert={onGeaendert}
                    bereiche={bereiche} aktiv={aktiv} markiert={markiert} onWaehlen={onWaehlen}
                    onMeldung={onMeldung} onGeloescht={onGeloescht} alsMahnung mitErstattet={mitErstattet} />
      ))}
      {loeschen && (
        <tr><td colSpan={spalten} style={{ padding: 0 }}>
          <LoeschDialog beleg={beleg} onClose={() => setLoeschen(false)}
                        onFertig={() => { setLoeschen(false); onGeloescht(zeile); }} />
        </td></tr>
      )}
    </>
  );
}

/** Welle 104: was die Versicherung erstattet hat — Summe der Abrechnungspositionen,
 *  dazu das Kennzeichen („<" teilweise, „>" gar nicht), das auch im Dateinamen steht. */
function erstattetBetrag(b) {
  const v = b.erstattet_summe != null ? b.erstattet_summe : b.erstattung;
  return v == null || v === "" ? null : Number(v);
}
function ErstattetZelle({ beleg }) {
  const flag = beleg.erstattet_flag;
  return (
    <>
      {eur(erstattetBetrag(beleg))}
      {flag === "teilweise" && (
        <span style={S.pillTeilweise} title="Dateiname beginnt mit „<“">{"< teilweise"}</span>
      )}
      {flag === "keine" && (
        <span style={S.pillWarn} title="Dateiname beginnt mit „>“">{"> nicht erstattet"}</span>
      )}
    </>
  );
}

/* ── Sektion (= Bereich) mit Bündeln ───────────────────────────────────── */
/** Spalten der Belegtabelle — Beschriftung und wie der Filterwert gebildet wird. */
/** `sort` liefert den Vergleichswert: Datum als ISO-Text, Betrag als Zahl, sonst Text. */
const SPALTEN = [
  { key: "datum_beleg", label: "Datum", wert: (b) => datum(b.datum_beleg), sort: (b) => String(b.datum_beleg || "") },
  { key: "arzt", label: "Behandler", wert: (b) => b.arzt || "" },
  { key: "person", label: "Person", wert: (b) => b.person || "" },
  { key: "betrag", label: "Betrag", wert: (b) => eur(b.betrag), rechts: true,
    sort: (b) => (b.betrag == null || b.betrag === "" ? null : Number(b.betrag)) },
  { key: "rechnungsnummer", label: "Rechnungsnr.", wert: (b) => b.rechnungsnummer || "" },
];
/** Nur in Bezahlt: erstatteter Betrag (hinter „Betrag"). */
const SPALTE_ERSTATTET = {
  key: "erstattet", label: "Erstattet", rechts: true,
  wert: (b) => {
    const e = eur(erstattetBetrag(b));
    return b.erstattet_flag && b.erstattet_flag !== "voll" ? `${e} ${ERSTATTET_ZEICHEN[b.erstattet_flag]}` : e;
  },
  sort: (b) => erstattetBetrag(b),
};
const spaltenFuer = (bereich) => (bereich === "bezahlt"
  ? [...SPALTEN.slice(0, 4), SPALTE_ERSTATTET, SPALTEN[4]] : SPALTEN);

/** Welle 104: Filter ganz oben — gelten über alle Ordner. */
const GLOBAL_SPALTEN = [
  ...SPALTEN,
  { key: "bereich", label: "Ordner", wert: (b) => BEREICH_NAME[b.bereich] || b.bereich || "" },
  { key: "erstattet_flag", label: "Erstattung", wert: (b) => ERSTATTET_NAME[b.erstattet_flag] || "" },
  { key: "dublette", label: "Dublette", wert: (b) => DUBLETTEN_NAME[b.dublette_stufe] || "" },
];

/** Freitextsuche: jedes Wort (Leerzeichen = UND) muss irgendwo im Beleg vorkommen. */
function suchText(b) {
  return [b.datei_name, b.arzt, b.person, b.rechnungsnummer, b.buendel, b.fachrichtung, b.leistung,
          b.vorgangsnummer, BEREICH_NAME[b.bereich], ERSTATTET_NAME[b.erstattet_flag],
          DUBLETTEN_NAME[b.dublette_stufe],
          datum(b.datum_beleg), b.datum_beleg, eur(b.betrag), b.betrag,
          eur(erstattetBetrag(b)), b.notiz,
          ...(b.mahnungen || []).map((m) => m.datei_name)]
    .filter((x) => x != null && x !== "").join(" ").toLowerCase();
}

/** Leere Werte stehen immer unten, egal in welche Richtung sortiert wird. */
function vergleiche(spalte, richtung) {
  const holen = spalte.sort || spalte.wert;
  return (a, c) => {
    const x = holen(a), y = holen(c);
    const leerX = x == null || x === "", leerY = y == null || y === "";
    if (leerX || leerY) return leerX === leerY ? 0 : leerX ? 1 : -1;
    const r = typeof x === "number" && typeof y === "number"
      ? x - y : String(x).localeCompare(String(y), "de", { numeric: true });
    return richtung === "desc" ? -r : r;
  };
}

function Sektion({ daten, offenStart, onStatus, onGeaendert, bereiche, aktiv, markiert, onWaehlen, onMeldung,
                   onGeloescht, onAnsicht, globalPasst, globalAktiv = false }) {
  const [offen, setOffen] = useState(offenStart);
  const spalten = useMemo(() => spaltenFuer(daten.bereich), [daten.bereich]);
  const mitErstattet = daten.bereich === "bezahlt";
  const [zuBuendel, setZuBuendel] = useState(() => new Set());
  const [filter, setFilter] = useState({});          // spalte → Set gewählter Werte
  const [sortierung, setSortierung] = useState(null); // { key, richtung }

  // Neue Sortierung/Filter = neue Reihenfolge: Auswahl auf die erste sichtbare Zeile
  const ersterLauf = useRef(true);
  useEffect(() => {
    if (ersterLauf.current) { ersterLauf.current = false; return; }
    onAnsicht && onAnsicht(daten.bereich);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sortierung]);

  // Klick auf die Beschriftung: aufsteigend → absteigend → aus
  const sortieren = (key) => setSortierung((alt) => {
    if (!alt || alt.key !== key) return { key, richtung: "asc" };
    return alt.richtung === "asc" ? { key, richtung: "desc" } : null;
  });

  // Sortiert wird innerhalb eines Bündels; Mahnungen hängen weiter an ihrer Rechnung
  const ordnen = useCallback((liste) => {
    if (!sortierung) return liste;
    const spalte = spalten.find((s) => s.key === sortierung.key);
    return spalte ? [...liste].sort(vergleiche(spalte, sortierung.richtung)) : liste;
  }, [sortierung, spalten]);
  const toggleB = (name) => setZuBuendel((p) => {
    const n = new Set(p); if (n.has(name)) n.delete(name); else n.add(name); return n;
  });

  // Welle 104: was der Filter/die Suche oben übrig lässt — daraus die Werte-Listen
  const alleBelege = useMemo(
    () => (daten.buendel || []).flatMap((b) => b.belege || [])
      .filter((b) => !globalPasst || globalPasst(b)), [daten, globalPasst]);

  // Auswahllisten je Spalte — wie in Excel: alles, was in dieser Spalte vorkommt
  const werte = useMemo(() => {
    const raus = {};
    spalten.forEach((s) => {
      raus[s.key] = [...new Set(alleBelege.map((b) => s.wert(b)))]
        .sort((a, c) => String(a).localeCompare(String(c), "de"));
    });
    return raus;
  }, [alleBelege, spalten]);

  const passt = useCallback((b) => (!globalPasst || globalPasst(b)) && spalten.every((s) => {
    const a = filter[s.key];
    return !a || a.size === 0 || a.has(s.wert(b));
  }), [filter, spalten, globalPasst]);

  const eigenerFilter = spalten.some((s) => filter[s.key] && filter[s.key].size > 0);
  const filterAktiv = eigenerFilter || globalAktiv;
  const gefiltert = alleBelege.filter(passt).length;

  // Suche/Filter oben: Sektionen mit Treffern klappen auf
  useEffect(() => {
    if (globalAktiv && gefiltert > 0) setOffen(true);
  }, [globalAktiv, gefiltert]);

  const kopfZeile = (
    <tr>
      {spalten.map((s) => (
        <FilterKopf key={s.key} label={s.label} rechts={s.rechts} werte={werte[s.key] || []}
                    auswahl={filter[s.key]}
                    onAendern={(neu) => setFilter((f) => ({ ...f, [s.key]: neu }))}
                    sortierung={sortierung?.key === s.key ? sortierung.richtung : null}
                    onSortieren={() => sortieren(s.key)} />
      ))}
      <th style={S.th}>Status</th>
      <th style={{ ...S.th, textAlign: "center" }}></th>
    </tr>
  );

  return (
    <div style={S.karte}>
      <div style={S.kopf} onClick={() => setOffen((o) => !o)}>
        <span style={{ fontSize: "1rem" }}>{offen ? "▾" : "▸"}</span>
        <h2 style={S.kopfTitel}>{daten.name}</h2>
        <span style={S.zaehler}>
          {filterAktiv
            ? `${gefiltert} von ${daten.anzahl} Belegen (gefiltert)`
            : `${daten.anzahl} ${daten.anzahl === 1 ? "Beleg" : "Belege"}`}
          {daten.offen_analyse > 0 && ` · ${daten.offen_analyse} nicht ausgewertet`}
        </span>
        {eigenerFilter && (
          <button style={{ ...S.btnMini, marginLeft: 10 }}
                  onClick={(e) => { e.stopPropagation(); setFilter({}); }}>
            Filter zurücksetzen
          </button>
        )}
      </div>
      {offen && daten.anzahl === 0 && (
        <div style={{ padding: "10px 16px", fontSize: "0.85rem", color: "var(--color-gray-500)" }}>
          Keine Dateien in diesem Bereich.
        </div>
      )}
      {offen && daten.buendel.map((b) => {
        const ohneBuendel = !b.name;
        const zu = zuBuendel.has(b.name);
        const sichtbar = b.belege.filter(passt);
        if (filterAktiv && !sichtbar.length) return null;   // leere Bündel beim Filtern ausblenden
        return (
          <div key={b.name || "_"}>
            {!ohneBuendel && (
              <div style={S.buendel} onClick={() => toggleB(b.name)}>
                <span>{zu ? "▸" : "▾"}</span>
                <span>📁 {b.name}</span>
                <span style={{ color: "var(--color-gray-600)", fontWeight: 400 }}>
                  {filterAktiv && sichtbar.length !== b.anzahl ? `${sichtbar.length} von ` : ""}
                  {b.anzahl} {b.anzahl === 1 ? "Beleg" : "Belege"}
                  {mitErstattet && (() => {
                    // Summen ohne die mitgewanderte Leistungsabrechnung, sonst zählt sie doppelt
                    const nurBelege = b.belege.filter((y) => y.art !== "leistungsabrechnung");
                    return ` · ${eur(nurBelege.reduce((x, y) => x + (Number(y.betrag) || 0), 0))}` +
                      ` · erstattet ${eur(nurBelege.reduce((x, y) => x + (erstattetBetrag(y) || 0), 0))}`;
                  })()}
                </span>
              </div>
            )}
            {!zu && (
              <table style={S.tabelle}>
                <thead>{kopfZeile}</thead>
                <tbody>
                  {ordnen(sichtbar).map((bl) => (
                    <BelegZeile key={bl.id} beleg={bl} sektion={daten.bereich} onStatus={onStatus}
                                onGeaendert={onGeaendert} bereiche={bereiche}
                                aktiv={aktiv} markiert={markiert} onWaehlen={onWaehlen}
                                onMeldung={onMeldung} onGeloescht={onGeloescht}
                                mitErstattet={mitErstattet} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Welle 104: Workflow anstoßen (Trockenlauf → Ausführen) ─────────────── */
function WorkflowDialog({ bericht, busy, onAusfuehren, onClose }) {
  const b = bericht.buchung || { belege: [], positionen: [], uebersprungen: [] };
  const umbenannt = (bericht.kennzeichen || []).filter((k) => k.umbenannt);
  const nachgetragen = (bericht.kennzeichen || []).filter((k) => !k.umbenannt && !k.fehler).length;
  const [offeneZeigen, setOffeneZeigen] = useState(false);
  const nichts = !b.belege.length && !umbenannt.length && !nachgetragen && !bericht.ungelesen;
  const kennzeichen = (flag) => flag === "teilweise" ? <span style={S.pillTeilweise}>{"< teilweise"}</span>
    : flag === "keine" ? <span style={S.pillWarn}>{"> nicht erstattet"}</span>
    : <span style={{ color: "var(--color-success)", fontSize: "0.78rem" }}>voll</span>;
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.dialog, width: 900, maxHeight: "84vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px" }}>
          {bericht.trockenlauf ? "Workflow: was würde passieren?" : "Workflow ausgeführt"}
        </h3>
        <p style={{ fontSize: "0.85rem", color: "var(--color-gray-600)", margin: "0 0 12px" }}>
          Leistungsabrechnungen mit den Belegen abgleichen: sichere Treffer (Rechnungsbetrag exakt und
          Datum oder Behandler) wandern nach Bezahlt. Teilweise erstattet bekommt „&lt;" vorne,
          gar nicht erstattet „&gt;".{bericht.trockenlauf && " Noch ist nichts passiert."}
        </p>
        {bericht.ungelesen > 0 && (
          <div style={S.info}>
            {bericht.ungelesen} Leistungsabrechnung{bericht.ungelesen === 1 ? " ist" : "en sind"} noch nicht
            gelesen — {bericht.trockenlauf ? "werden beim Ausführen" : "wurden"} mit Gemini gelesen (höchstens 10).
          </div>
        )}
        <h4 style={{ margin: "12px 0 6px" }}>
          {bericht.trockenlauf ? "Wird bezahlt gemeldet" : "Bezahlt gemeldet"} ({b.belege.length})
        </h4>
        {b.belege.length === 0 ? (
          <div style={{ fontSize: "0.85rem", color: "var(--color-gray-500)" }}>Keine neuen sicheren Treffer.</div>
        ) : (
          <table style={S.tabelle}>
            <thead><tr>
              <th style={S.th}>Beleg</th><th style={{ ...S.th, textAlign: "right" }}>Rechnung</th>
              <th style={{ ...S.th, textAlign: "right" }}>Erstattet</th><th style={S.th}>Kennzeichen</th>
              <th style={S.th}>Positionen</th>
            </tr></thead>
            <tbody>
              {b.belege.map((x) => (
                <tr key={x.beleg_id}>
                  <td style={{ ...S.td, wordBreak: "break-all" }}>{x.datei_neu || x.beleg}
                    {x.fehler && <div style={{ color: "var(--color-error)" }}>{x.fehler}</div>}</td>
                  <td style={{ ...S.td, ...S.num }}>{eur(x.rechnung)}</td>
                  <td style={{ ...S.td, ...S.num }}>{eur(x.erstattet)}</td>
                  <td style={S.td}>{kennzeichen(x.flag)}</td>
                  <td style={S.td}>{x.positionen}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {umbenannt.length > 0 && (
          <>
            <h4 style={{ margin: "14px 0 6px" }}>Kennzeichen im Bestand (Bezahlt) — {umbenannt.length} Dateien umbenennen</h4>
            <table style={S.tabelle}>
              <thead><tr>
                <th style={S.th}>neuer Dateiname</th><th style={{ ...S.th, textAlign: "right" }}>Rechnung</th>
                <th style={{ ...S.th, textAlign: "right" }}>Erstattet</th><th style={S.th}>Kennzeichen</th>
              </tr></thead>
              <tbody>
                {umbenannt.map((k) => (
                  <tr key={k.id}>
                    <td style={{ ...S.td, wordBreak: "break-all" }}>{k.neu}</td>
                    <td style={{ ...S.td, ...S.num }}>{eur(k.betrag)}</td>
                    <td style={{ ...S.td, ...S.num }}>{eur(k.erstattet)}</td>
                    <td style={S.td}>{kennzeichen(k.flag)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {nachgetragen > 0 && (
          <p style={{ fontSize: "0.82rem", color: "var(--color-gray-600)" }}>
            Bei {nachgetragen} Belegen in Bezahlt wird nur der erstattete Betrag nachgetragen (Dateiname bleibt).
          </p>
        )}
        {(bericht.offen || []).length > 0 && (
          <p style={{ fontSize: "0.82rem", color: "var(--color-gray-600)" }}>
            {bericht.offen.length} Abrechnungspositionen bleiben offen (kein sicherer Beleg) — Zuordnung per Klick in
            „Leistungsabrechnungen".{" "}
            <button style={S.btnMini} onClick={() => setOffeneZeigen((o) => !o)}>
              {offeneZeigen ? "ausblenden" : "anzeigen"}
            </button>
          </p>
        )}
        {offeneZeigen && (
          <table style={S.tabelle}>
            <thead><tr>
              <th style={S.th}>Datum</th><th style={S.th}>Behandler</th><th style={S.th}>Person</th>
              <th style={{ ...S.th, textAlign: "right" }}>Rechnung</th><th style={S.th}>bester Vorschlag</th>
            </tr></thead>
            <tbody>
              {bericht.offen.map((o) => (
                <tr key={o.id}>
                  <td style={S.td}>{datum(o.datum_beleg)}</td><td style={S.td}>{o.arzt || "—"}</td>
                  <td style={S.td}>{o.person || "—"}</td>
                  <td style={{ ...S.td, ...S.num }}>{eur(o.beleg_betrag)}</td>
                  <td style={{ ...S.td, fontSize: "0.78rem", color: "var(--color-gray-600)" }}>
                    {o.vorschlag ? `${o.vorschlag} (${Math.round(o.vorschlag_score || 0)} P: ${o.vorschlag_grund})` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button style={S.btn} onClick={onClose} disabled={busy}>{bericht.trockenlauf ? "Abbrechen" : "Schließen"}</button>
          {bericht.trockenlauf && (
            <button style={S.btnPrim} onClick={onAusfuehren} disabled={busy || nichts}>
              {busy ? "Läuft…" : "Ausführen"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Welle 105: Dublettenprüfung über alle Ordner ───────────────────────── */
/** Kleiner Kreisel für Knöpfe (die Keyframes `spin` kommen aus index.css). */
export function Kreisel({ gross = false }) {
  const d = gross ? 28 : 13;
  return (
    <span style={{ display: "inline-block", width: d, height: d, borderRadius: "50%",
                   border: `${gross ? 3 : 2}px solid currentColor`, borderTopColor: "transparent",
                   animation: "spin 0.8s linear infinite", verticalAlign: "-2px", marginRight: 6 }} />
  );
}

/** Deckt den Inhalt zu, solange etwas läuft — mit Fortschritt, wenn bekannt. */
function LadeSchleier({ text, fortschritt }) {
  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 5,
                  display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", gap: 12, borderRadius: 10 }}>
      <span style={{ color: "#fff" }}><Kreisel gross /></span>
      <div style={{ color: "#fff", fontWeight: 700 }}>
        {fortschritt ? `${fortschritt.text || text} ${fortschritt.fertig} von ${fortschritt.gesamt}` : text}
      </div>
      {fortschritt && (
        <div style={{ width: 280, height: 8, borderRadius: 4, background: "rgba(255,255,255,0.25)", overflow: "hidden" }}>
          <div style={{ height: "100%", background: "#fff", transition: "width 0.2s",
                        width: `${Math.round(100 * fortschritt.fertig / Math.max(1, fortschritt.gesamt))}%` }} />
        </div>
      )}
    </div>
  );
}

export function istMahnung(b) {
  return (b.art === "mahnung") || b.bereich === "mahnung" || !!b.mahnung_zu;
}

export const DUBLETTEN_NAME = {
  sicher: "sicher doppelt", wahrscheinlich: "höchstwahrscheinlich doppelt", moeglich: "möglich doppelt",
};

const DUBLETTEN_TITEL = {
  sicher: "Sicher doppelt — kann gelöscht werden",
  wahrscheinlich: "Höchstwahrscheinlich doppelt — bitte kurz schauen",
  moeglich: "Möglich, aber nicht sicher",
};

function PdfSpalte({ beleg }) {
  const [url, setUrl] = useState(null);
  const [fehler, setFehler] = useState("");
  useEffect(() => {
    let weg = false, blobUrl = null;
    fetch(`${API}/belege/${beleg.id}/datei`)
      .then((r) => (r.ok ? r.blob() : r.text().then((t) => { throw new Error(t); })))
      .then((b) => {
        if (weg) return;
        blobUrl = URL.createObjectURL(new Blob([b], { type: "application/pdf" }));
        setUrl(blobUrl);
      })
      .catch((e) => { if (!weg) setFehler(String(e.message || e).slice(0, 200)); });
    return () => { weg = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [beleg.id]);
  return (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
                  border: "1px solid var(--color-gray-300)", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "6px 10px", borderBottom: "1px solid var(--color-gray-200)", fontSize: "0.78rem" }}>
        <strong>{beleg.bereich_name}</strong>{beleg.buendel ? ` · ${beleg.buendel}` : ""}
        <div style={{ wordBreak: "break-all", color: "var(--color-gray-600)" }}>{beleg.datei}</div>
      </div>
      {fehler && <div style={{ ...S.err, margin: 8 }}>{fehler}</div>}
      {!url && !fehler && <div style={{ padding: 16, fontSize: "0.82rem" }}>Lade PDF…</div>}
      {url && <iframe title={beleg.datei} src={url} style={{ flex: 1, border: "none", background: "#525659" }} />}
    </div>
  );
}

/** Welle 105: beide Dateien nebeneinander ansehen — und gleich hier entscheiden. */
function DublettenVergleich({ gruppe, behaltenId, busy, fortschritt, auswahl, onWaehlen, onBehalten,
                             onKeine, onZusammen, onLoeschenJetzt, onSchliessen }) {
  const alle = [gruppe.behalten, ...gruppe.loeschen];
  // Bei mehr als zwei Dateien entscheidet das Häkchen je Datei, welche wirklich weg soll
  const weg = alle.filter((b) => b.id !== behaltenId && auswahl.has(b.id)).map((b) => b.id);
  return (
    <div style={{ ...S.overlay, zIndex: 1100 }} onClick={onSchliessen}>
      <div style={{ background: "var(--bg-card)", borderRadius: 10, padding: 14, width: "94vw",
                    height: "92vh", display: "flex", flexDirection: "column", gap: 10, position: "relative" }}
           onClick={(e) => e.stopPropagation()}>
        {busy && <LadeSchleier text="Arbeite…" fortschritt={fortschritt} />}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0 }}>Nebeneinander ansehen</h3>
          <span style={{ fontSize: "0.82rem", color: "var(--color-gray-600)" }}>{gruppe.grund}</span>
          {/* Direkt hier entscheiden — man sieht ja gerade beide Dateien */}
          <button style={{ ...S.btn, marginLeft: "auto" }} disabled={busy}
                  title="Diese Dateien sind verschieden — nie wieder melden"
                  onClick={() => onKeine(alle.map((b) => b.id))}>
            {busy && <Kreisel />}keine Dublette
          </button>
          <button style={S.btn} disabled={busy || !weg.length}
                  title="Die Seiten der anderen Datei hinten anhängen und sie löschen"
                  onClick={() => onZusammen(behaltenId, weg)}>
            {busy && <Kreisel />}
            {weg.length > 1 ? `${weg.length} Dateien anhängen` : "zu einer Datei zusammenfügen"}
          </button>
          <button style={S.btnRot} disabled={busy || !weg.length}
                  title="Die rot markierte Datei in den Dropbox-Papierkorb legen"
                  onClick={() => onLoeschenJetzt(behaltenId, weg)}>
            {busy && <Kreisel />}
            {weg.length === 1 ? "diese Datei löschen" : `${weg.length} Dateien löschen`}
          </button>
          <button style={S.btn} onClick={onSchliessen}>Schließen</button>
        </div>
        <div style={{ display: "flex", gap: 10, flex: 1, minHeight: 0 }}>
          {alle.map((b) => (
            <div key={b.id} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center", fontSize: "0.82rem", fontWeight: 700,
                            color: b.id === behaltenId ? "var(--color-success)"
                                   : auswahl.has(b.id) ? "var(--color-error)" : "var(--color-gray-500)" }}>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                  <input type="radio" checked={b.id === behaltenId} onChange={() => onBehalten(b.id)} />
                  {b.id === behaltenId ? "diese bleibt"
                    : auswahl.has(b.id) ? "diese wird gelöscht" : "diese bleibt vorerst liegen"}
                </label>
                {b.id !== behaltenId && (
                  <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer",
                                  fontWeight: 400, color: "var(--color-gray-600)" }}
                         title="Nur angehakte Dateien werden gelöscht oder angehängt">
                    <input type="checkbox" checked={auswahl.has(b.id)} onChange={() => onWaehlen(b.id)} />
                    auswählen
                  </label>
                )}
              </div>
              <PdfSpalte beleg={b} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DublettenZeile({ eintrag, weg, gewaehlt, onWaehlen, onBehalten }) {
  return (
    <tr style={weg ? null : { background: "var(--color-gray-100)" }}>
      <td style={{ ...S.td, width: 30 }}>
        {weg && <input type="checkbox" checked={gewaehlt} onChange={onWaehlen} />}
      </td>
      <td style={{ ...S.td, whiteSpace: "nowrap", fontWeight: 700,
                   color: weg ? "var(--color-error)" : "var(--color-success)" }}>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}
               title="Diese Datei behalten">
          <input type="radio" checked={!weg} onChange={onBehalten} />
          {weg ? "wird gelöscht" : "bleibt"}
        </label>
      </td>
      <td style={S.td}>{eintrag.bereich_name}{eintrag.buendel ? ` · ${eintrag.buendel}` : ""}</td>
      <td style={S.td}>{eintrag.datum ? datum(eintrag.datum) : "—"}</td>
      <td style={{ ...S.td, ...S.num }}>{eur(eintrag.betrag)}</td>
      <td style={S.td}>{eintrag.nummer || "—"}</td>
      <td style={{ ...S.td, wordBreak: "break-all" }}>{eintrag.datei}</td>
    </tr>
  );
}

function DublettenDialog({ bericht, auswahl, setAuswahl, busy, ergebnis, fortschritt,
                          onLoeschen, onKeine, onZusammen, onLoeschenJetzt, onClose }) {
  const roh = [...(bericht.gruppen || []),
               ...(bericht.abrechnungen || []).map((g) => ({ ...g, abrechnung: true }))];
  // Welle 105: Welche Datei bleibt, lässt sich je Fall umstellen (Vorschlag = Server).
  const [behalten, setBehalten] = useState({});
  const [vergleich, setVergleich] = useState(null);
  const schluessel = (g, i) => `${g.stufe}-${g.behalten.id}-${i}`;
  const bleibtId = (g, i) => behalten[schluessel(g, i)] ?? g.behalten.id;

  const behaltenSetzen = (g, i, id) => {
    const k = schluessel(g, i);
    const vorher = bleibtId(g, i);
    setBehalten((alt) => ({ ...alt, [k]: id }));
    setAuswahl((alt) => {
      const n = new Set(alt);
      if (n.has(id)) { n.delete(id); n.add(vorher); }   // Häkchen wandert mit
      return n;
    });
  };
  const gruppen = roh;
  const umschalten = (id) => setAuswahl((alt) => {
    const n = new Set(alt); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const alleStufe = (stufe, an) => setAuswahl((alt) => {
    const n = new Set(alt);
    gruppen.forEach((g, i) => {
      if (g.stufe !== stufe) return;
      const bleibt = bleibtId(g, i);
      [g.behalten, ...g.loeschen].forEach((x) => {
        if (x.id === bleibt) { n.delete(x.id); return; }
        if (an) n.add(x.id); else n.delete(x.id);
      });
    });
    return n;
  });
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.dialog, width: 1000, maxHeight: "86vh", overflow: "auto", position: "relative" }}
           onClick={(e) => e.stopPropagation()}>
        {busy && <LadeSchleier text="Arbeite…" fortschritt={fortschritt} />}
        <h3 style={{ margin: "0 0 4px" }}>Dubletten prüfen</h3>
        <p style={{ fontSize: "0.85rem", color: "var(--color-gray-600)", margin: "0 0 12px" }}>
          Verglichen werden alle KRV-Ordner (auch Einzureichen gegen Bezahlt), Mahnungen bleiben außen vor.
          Ausgewählte Dateien wandern in den Dropbox-Papierkorb und sind dort 30 Tage wiederherstellbar.
        </p>
        {ergebnis && (
          <div style={S.info}>
            {ergebnis.entfernt} Datei(en) gelöscht{ergebnis.fehler ? `, ${ergebnis.fehler} Fehler` : ""}:
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {(ergebnis.details || []).map((d, i) => (
                <li key={i} style={{ fontSize: "0.8rem" }}>{d.datei || `Fehler: ${d.fehler}`}</li>
              ))}
            </ul>
          </div>
        )}
        {["sicher", "wahrscheinlich", "moeglich"].map((stufe) => {
          const teil = gruppen.filter((g) => g.stufe === stufe);
          if (!teil.length) return null;
          const anzahl = teil.reduce((n, g) => n + g.loeschen.length, 0);
          return (
            <details key={stufe} open={stufe !== "moeglich"} style={{ marginBottom: 12 }}>
              <summary style={{ cursor: "pointer", fontWeight: 700, margin: "10px 0 6px" }}>
                {DUBLETTEN_TITEL[stufe]} — {anzahl} {anzahl === 1 ? "Datei" : "Dateien"}
                <button style={{ ...S.btnMini }} onClick={(e) => { e.preventDefault(); alleStufe(stufe, true); }}>alle</button>
                <button style={{ ...S.btnMini }} onClick={(e) => { e.preventDefault(); alleStufe(stufe, false); }}>keine</button>
              </summary>
              <table style={S.tabelle}>
                <thead><tr>
                  <th style={S.th}></th><th style={S.th}></th><th style={S.th}>Ordner</th>
                  <th style={S.th}>Datum</th><th style={{ ...S.th, textAlign: "right" }}>Betrag</th>
                  <th style={S.th}>Rechnungsnr.</th><th style={S.th}>Datei</th>
                </tr></thead>
                <tbody>
                  {teil.map((g) => {
                    const i = gruppen.indexOf(g);
                    const bleibt = bleibtId(g, i);
                    return (
                      <React.Fragment key={schluessel(g, i)}>
                        <tr><td colSpan={7} style={{ ...S.td, background: "var(--color-gray-100)",
                                                     fontSize: "0.78rem", color: "var(--color-gray-600)" }}>
                          {g.abrechnung ? "Leistungsabrechnung · " : ""}{g.grund}
                          <button style={{ ...S.btnMini }} onClick={() => setVergleich({ gruppe: g, i })}>
                            nebeneinander ansehen
                          </button>
                          <button style={{ ...S.btnMini }} disabled={busy}
                                  title="Diese Dateien sind verschieden — nie wieder melden"
                                  onClick={() => onKeine([g.behalten, ...g.loeschen].map((x) => x.id))}>
                            {busy && <Kreisel />}keine Dublette
                          </button>
                          <button style={{ ...S.btnMini }} disabled={busy}
                                  title="Die Seiten der anderen Datei(en) hinten anhängen und sie löschen"
                                  onClick={() => onZusammen(bleibt, [g.behalten, ...g.loeschen]
                                    .filter((x) => x.id !== bleibt).map((x) => x.id))}>
                            {busy && <Kreisel />}zu einer Datei zusammenfügen
                          </button>
                        </td></tr>
                        {[g.behalten, ...g.loeschen].map((x) => (
                          <DublettenZeile key={x.id} eintrag={x} weg={x.id !== bleibt}
                                          gewaehlt={auswahl.has(x.id)}
                                          onWaehlen={() => umschalten(x.id)}
                                          onBehalten={() => behaltenSetzen(g, i, x.id)} />
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </details>
          );
        })}
        {!gruppen.length && (
          <div style={{ fontSize: "0.9rem", color: "var(--color-gray-500)" }}>Keine Dubletten gefunden.</div>
        )}
        {vergleich && (
          <DublettenVergleich gruppe={vergleich.gruppe} behaltenId={bleibtId(vergleich.gruppe, vergleich.i)}
                              busy={busy} fortschritt={fortschritt} auswahl={auswahl} onWaehlen={umschalten}
                              onBehalten={(id) => behaltenSetzen(vergleich.gruppe, vergleich.i, id)}
                              onKeine={(ids) => onKeine(ids).then(() => setVergleich(null))}
                              onZusammen={(b, w) => onZusammen(b, w).then(() => setVergleich(null))}
                              onLoeschenJetzt={(b, w) => onLoeschenJetzt(b, w).then(() => setVergleich(null))}
                              onSchliessen={() => setVergleich(null)} />
        )}
        {fortschritt && (
          <div style={{ margin: "12px 0" }}>
            <div style={{ fontSize: "0.82rem", marginBottom: 4 }}>
              {fortschritt.text || "Lösche…"} {fortschritt.fertig} von {fortschritt.gesamt}
            </div>
            <div style={{ height: 8, borderRadius: 4, background: "var(--color-gray-200)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.round(100 * fortschritt.fertig / Math.max(1, fortschritt.gesamt))}%`,
                            background: "var(--color-info)", transition: "width 0.2s" }} />
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button style={S.btn} onClick={onClose} disabled={busy}>Schließen</button>
          <button style={S.btnRot} onClick={onLoeschen} disabled={busy || !auswahl.size}>
            {busy ? <><Kreisel />Lösche…</>
                  : `${auswahl.size} ${auswahl.size === 1 ? "Datei" : "Dateien"} löschen (Papierkorb)`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Seite ─────────────────────────────────────────────────────────────── */
export default function KrvPage() {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);   // { belege, ziel }
  const [workflow, setWorkflow] = useState(null); // Bericht des Trockenlaufs / Laufs
  // Welle 105: Dublettenprüfung + Mahnungen ausblenden (Standard: aus)
  const [dubletten, setDubletten] = useState(null);
  const [dublettenWahl, setDublettenWahl] = useState(() => new Set());
  const [dublettenErgebnis, setDublettenErgebnis] = useState(null);
  const [dublettenFortschritt, setDublettenFortschritt] = useState(null);
  const [mahnungenZeigen, setMahnungenZeigen] = useState(() => {
    try { return localStorage.getItem("krvMahnungen") === "1"; } catch (_) { return false; }
  });
  // Welle 104: Suche und Filter ganz oben — gelten über alle Ordner
  const [suche, setSuche] = useState("");
  const [gFilter, setGFilter] = useState({});      // spalte → Set gewählter Werte

  const laden = useCallback(async () => {
    try { setDaten(await api("/belege")); setFehler(""); }
    catch (e) { setFehler(String(e.message || e)); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const scan = async () => {
    setBusy(true); setMeldung("");
    try {
      const r = await api("/belege/scan", "POST");
      setMeldung(`Ordner abgeglichen: ${r.neu} neu, ${r.aktualisiert} aktualisiert.`);
      await laden();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const offeneAuswerten = async () => {
    setBusy(true); setMeldung("");
    try {
      const r = await api("/belege/analysieren-offene?limit=10", "POST");
      setMeldung(`${r.verarbeitet} Belege ausgewertet${r.fehler ? `, ${r.fehler} Fehler` : ""}` +
                 (r.offen_danach ? ` — noch ${r.offen_danach} offen.` : "."));
      await laden();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const workflowPruefen = async () => {
    setBusy(true); setMeldung(""); setFehler("");
    try { setWorkflow(await api("/workflow?trockenlauf=1", "POST")); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };
  const workflowAusfuehren = async () => {
    setBusy(true);
    try {
      const r = await api("/workflow?trockenlauf=0", "POST");
      setWorkflow(r);
      const k = (r.kennzeichen || []).filter((x) => x.umbenannt).length;
      setMeldung(`Workflow: ${r.buchung.belege.length} Belege nach Bezahlt, ${k} Kennzeichen gesetzt, ` +
                 `${(r.offen || []).length} Positionen offen.`);
      await laden();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const dublettenPruefen = async () => {
    setBusy(true); setMeldung(""); setFehler(""); setDublettenErgebnis(null);
    try {
      const r = await api("/dubletten", "GET");
      setDubletten(r);
      const sicher = new Set();
      [...(r.gruppen || []), ...(r.abrechnungen || [])]
        .filter((g) => g.stufe === "sicher")
        .forEach((g) => g.loeschen.forEach((x) => sicher.add(x.id)));
      setDublettenWahl(sicher);
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  // In kleinen Häppchen löschen — so bewegt sich der Fortschrittsbalken und ein Fehler
  // am Ende wirft nicht die schon gelöschten Dateien um.
  const dublettenLoeschen = async (nurDiese = null) => {
    const ids = Array.isArray(nurDiese) ? nurDiese : [...dublettenWahl];
    if (!ids.length) return;
    setBusy(true);
    setDublettenFortschritt({ fertig: 0, gesamt: ids.length, text: "Lösche" });
    const gesamt = { entfernt: 0, fehler: 0, details: [] };
    try {
      for (let i = 0; i < ids.length; i += 3) {
        const r = await api("/dubletten/loeschen", "POST", { ids: ids.slice(i, i + 3) });
        gesamt.entfernt += r.entfernt || 0;
        gesamt.fehler += r.fehler || 0;
        gesamt.details = gesamt.details.concat(r.details || []);
        setDublettenFortschritt({ fertig: Math.min(i + 3, ids.length), gesamt: ids.length, text: "Lösche" });
      }
      setDublettenErgebnis(gesamt);
      setMeldung(`${gesamt.entfernt} Dublette(n) in den Dropbox-Papierkorb gelegt` +
                 (gesamt.fehler ? `, ${gesamt.fehler} Fehler.` : "."));
      setDubletten(await api("/dubletten", "GET"));
      setDublettenWahl((alt) => {
        if (!Array.isArray(nurDiese)) return new Set();
        const n = new Set(alt); nurDiese.forEach((i) => n.delete(i)); return n;
      });
      await laden();
    } catch (e) { setFehler(String(e.message || e)); }
    setDublettenFortschritt(null);
    setBusy(false);
  };

  const dublettenKeine = async (ids) => {
    setBusy(true);
    setDublettenFortschritt({ fertig: 0, gesamt: 1, text: "Merke „keine Dublette“" });
    try {
      await api("/dubletten/keine", "POST", { ids });
      setMeldung("Als „keine Dublette“ gemerkt — dieser Fall wird nicht mehr gemeldet.");
      setDubletten(await api("/dubletten", "GET"));
      setDublettenWahl((alt) => { const n = new Set(alt); ids.forEach((i) => n.delete(i)); return n; });
      setDublettenFortschritt({ fertig: 1, gesamt: 1, text: "Merke „keine Dublette“" });
      await laden();
    } catch (e) { setFehler(String(e.message || e)); }
    setDublettenFortschritt(null);
    setBusy(false);
  };

  const dublettenZusammen = async (behalten, weitere) => {
    if (!window.confirm(`Die Seiten von ${weitere.length} Datei(en) werden hinten an die behaltene `
        + "angehängt, die andere(n) wandern in den Dropbox-Papierkorb. Fortfahren?")) return;
    setBusy(true);
    setDublettenFortschritt({ fertig: 0, gesamt: 1, text: "Füge zusammen" });
    try {
      const r = await api("/dubletten/zusammenfuegen", "POST", { behalten, weitere });
      setMeldung(`Zusammengefügt: „${r.datei}" hat jetzt ${r.seiten} Seiten` +
                 (r.entfernt?.length ? `, ${r.entfernt.length} Datei(en) im Papierkorb.` : "."));
      setDublettenFortschritt({ fertig: 1, gesamt: 1, text: "Füge zusammen" });
      setDubletten(await api("/dubletten", "GET"));
      setDublettenWahl((alt) => { const n = new Set(alt); weitere.forEach((i) => n.delete(i)); return n; });
      await laden();
    } catch (e) { setFehler(String(e.message || e)); }
    setDublettenFortschritt(null);
    setBusy(false);
  };

  const mahnungenUmschalten = () => setMahnungenZeigen((alt) => {
    try { localStorage.setItem("krvMahnungen", alt ? "0" : "1"); } catch (_) { /* egal */ }
    return !alt;
  });

  // Mahnungen ausblenden: sie stehen doppelt — als eigene Zeile im Ordner „Mahnungen"
  // und eingerückt unter ihrer Rechnung. Beides muss weg.
  const datenAnzeige = useMemo(() => {
    if (!daten || mahnungenZeigen) return daten;
    const bereiche = (daten.bereiche || []).map((b) => {
      const buendel = (b.buendel || []).map((bu) => ({
        ...bu,
        belege: (bu.belege || []).filter((x) => !istMahnung(x)).map((x) => ({ ...x, mahnungen: [] })),
      })).filter((bu) => bu.belege.length);
      return { ...b, buendel, anzahl: buendel.reduce((n, bu) => n + bu.belege.length, 0) };
    });
    return { ...daten, bereiche };
  }, [daten, mahnungenZeigen]);

  // ── Suche + Filter über alle Ordner ──
  const alleZeilen = useMemo(() => (datenAnzeige?.bereiche || [])
    .flatMap((b) => (b.buendel || []).flatMap((bu) => bu.belege || [])), [datenAnzeige]);
  const gWerte = useMemo(() => {
    const raus = {};
    GLOBAL_SPALTEN.forEach((sp) => {
      raus[sp.key] = [...new Set(alleZeilen.map((b) => sp.wert(b)))]
        .sort((a, c) => String(a).localeCompare(String(c), "de"));
    });
    return raus;
  }, [alleZeilen]);
  const woerter = useMemo(() => suche.toLowerCase().split(/\s+/).filter(Boolean), [suche]);
  const gFilterAn = GLOBAL_SPALTEN.some((sp) => gFilter[sp.key] && gFilter[sp.key].size > 0);
  const globalAktiv = gFilterAn || woerter.length > 0;
  const globalPasst = useMemo(() => {
    if (!globalAktiv) return null;
    return (b) => {
      if (!GLOBAL_SPALTEN.every((sp) => {
        const a = gFilter[sp.key];
        return !a || a.size === 0 || a.has(sp.wert(b));
      })) return false;
      if (!woerter.length) return true;
      const t = suchText(b);
      return woerter.every((w) => t.includes(w));
    };
  }, [globalAktiv, gFilter, woerter]);
  const treffer = globalPasst ? alleZeilen.filter(globalPasst).length : alleZeilen.length;
  const mitGlobal = (b) => (!globalPasst ? b : {
    ...b, buendel: (b.buendel || []).map((bu) => ({ ...bu, belege: (bu.belege || []).filter(globalPasst) }))
      .filter((bu) => bu.belege.length),
  });

  // ── Auswahl: aktive Zeile (Rahmen + Vorschau) und Markierung (wie Excel) ──
  const [sel, setSel] = useState({ aktiv: null, markiert: new Set(), anker: null });
  const [vorschauZu, setVorschauZu] = useState(false);     // Esc schließt, Klick öffnet wieder
  const startGesetzt = useRef(false);
  const naechsteZeile = useRef(null);                       // nach Verschieben/Löschen

  /** Alle Zeilenschlüssel in der Reihenfolge, in der sie zu sehen sind. */
  const sichtbareZeilen = () =>
    [...document.querySelectorAll("tr[data-krv-zeile]")].map((t) => t.dataset.krvZeile);
  const idAus = (zeile) => Number(String(zeile || "").split("|")[1]);

  const bereich = (liste, von, bis) => {
    const i = liste.indexOf(von), j = liste.indexOf(bis);
    if (i < 0 || j < 0) return new Set([bis]);
    return new Set(liste.slice(Math.min(i, j), Math.max(i, j) + 1));
  };

  const hinScrollen = (zeile) => {
    const el = document.querySelector(`tr[data-krv-zeile="${CSS.escape(zeile)}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  };

  // Klick: einzeln · Shift: Bereich ab Anker · Strg/Cmd: einzelne Zeile dazu/weg
  const waehlen = useCallback((zeile, e = {}) => {
    setVorschauZu(false);
    setSel((alt) => {
      if (e.shiftKey && alt.anker) {
        return { aktiv: zeile, anker: alt.anker, markiert: bereich(sichtbareZeilen(), alt.anker, zeile) };
      }
      if (e.metaKey || e.ctrlKey) {
        const m = new Set(alt.markiert);
        if (m.has(zeile)) m.delete(zeile); else m.add(zeile);
        return { aktiv: zeile, anker: zeile, markiert: m };
      }
      return { aktiv: zeile, anker: zeile, markiert: new Set([zeile]) };
    });
  }, []);

  // Beim Start (und wenn die aktive Zeile nach dem Neuladen fehlt): auf die gemerkte
  // nächste Zeile bzw. die erste sichtbare setzen
  useEffect(() => {
    if (!daten) return;
    const id = requestAnimationFrame(() => {
      const liste = sichtbareZeilen();
      if (!liste.length) return;
      const ziel = naechsteZeile.current;
      naechsteZeile.current = null;
      const erster = !startGesetzt.current;
      startGesetzt.current = true;
      setSel((alt) => {
        if (ziel && liste.includes(ziel)) {
          hinScrollen(ziel);
          return { aktiv: ziel, anker: ziel, markiert: new Set([ziel]) };
        }
        if (erster || (alt.aktiv && !liste.includes(alt.aktiv))) {
          return { aktiv: liste[0], anker: liste[0], markiert: new Set([liste[0]]) };
        }
        return alt;
      });
    });
    return () => cancelAnimationFrame(id);
  }, [daten]);

  /** Vor dem Verschieben/Löschen merken, welche Zeile danach dran ist: die erste
   *  sichtbare Zeile unterhalb der letzten betroffenen, die selbst nicht betroffen ist. */
  const naechsteMerken = (betroffeneIds) => {
    const liste = sichtbareZeilen();
    const weg = (z) => betroffeneIds.has(idAus(z));
    let letzte = -1;
    liste.forEach((z, i) => { if (weg(z)) letzte = i; });
    const danach = liste.slice(letzte + 1).find((z) => !weg(z));
    const davor = liste.slice(0, Math.max(letzte, 0)).reverse().find((z) => !weg(z));
    naechsteZeile.current = danach || davor || null;
  };

  const belegNachId = useMemo(() => {
    const m = new Map();
    (daten?.bereiche || []).forEach((b) => (b.buendel || []).forEach((bu) => (bu.belege || []).forEach((x) => {
      m.set(x.id, x);
      (x.mahnungen || []).forEach((y) => m.set(y.id, y));
    })));
    return m;
  }, [daten]);

  // Statuswechsel: gilt für alle markierten Zeilen, wenn die geänderte Zeile dazugehört.
  // Mahnung/Sonstiges/Steuer sofort, alle anderen Ziele über den Dialog.
  const statusWechseln = useCallback(async (beleg, ziel, zeile) => {
    const gruppe = sel.markiert.has(zeile) && sel.markiert.size > 1
      ? [...new Set([...sel.markiert].map(idAus))].map((id) => belegNachId.get(id)).filter(Boolean)
      : [beleg];
    const betroffen = gruppe.filter((b) => b.bereich !== ziel);
    if (!betroffen.length) return;
    if (!BEREICHE_DIREKT.includes(ziel)) { setDialog({ belege: betroffen, ziel }); return; }
    setMeldung(""); setFehler("");
    naechsteMerken(new Set(betroffen.map((b) => b.id)));
    let ok = 0;
    try {
      for (const b of betroffen) { await api(`/belege/${b.id}/status`, "POST", { bereich: ziel }); ok += 1; }
      setMeldung(betroffen.length === 1
        ? `„${beleg.datei_name}" → ${BEREICH_NAME[ziel] || ziel}`
        : `${ok} Dateien → ${BEREICH_NAME[ziel] || ziel}`);
    } catch (e) { setFehler(`${ok} von ${betroffen.length} verschoben — ${e.message || e}`); }
    await laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, belegNachId, laden]);

  const ansichtGeaendert = useCallback((sektion) => {
    requestAnimationFrame(() => {
      const erste = sichtbareZeilen().find((z) => z.startsWith(`${sektion}|`));
      if (!erste) return;
      setSel({ aktiv: erste, anker: erste, markiert: new Set([erste]) });
      hinScrollen(erste);
    });
  }, []);

  const nachLoeschen = useCallback((zeile) => {
    naechsteMerken(new Set([idAus(zeile)]));
    laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laden]);

  // Tastatur: ↑/↓ nächste sichtbare Zeile, Shift markiert mit, Esc schließt die Vorschau.
  // Nicht, solange ein Eingabe-/Auswahlfeld den Fokus hat oder ein Dialog offen ist.
  useEffect(() => {
    const taste = (e) => {
      const t = e.target;
      const tag = (t && t.tagName) || "";
      if (["INPUT", "SELECT", "TEXTAREA"].includes(tag) || (t && t.isContentEditable)) return;
      if (dialog || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === "Escape") { setVorschauZu(true); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const liste = sichtbareZeilen();
      if (!liste.length) return;
      e.preventDefault();
      setVorschauZu(false);
      setSel((alt) => {
        const i = liste.indexOf(alt.aktiv);
        const j = i < 0 ? 0 : Math.max(0, Math.min(liste.length - 1, i + (e.key === "ArrowDown" ? 1 : -1)));
        const neu = liste[j];
        hinScrollen(neu);
        if (e.shiftKey) {
          const anker = alt.anker && liste.includes(alt.anker) ? alt.anker : alt.aktiv || neu;
          return { aktiv: neu, anker, markiert: bereich(liste, anker, neu) };
        }
        return { aktiv: neu, anker: neu, markiert: new Set([neu]) };
      });
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [dialog]);

  const gewaehltBeleg = sel.aktiv && !vorschauZu ? belegNachId.get(idAus(sel.aktiv)) || null : null;

  const [vorschau, setVorschau] = useState(null);   // Trockenlauf „Nicht zugeordnet"

  const loseVorschau = async () => {
    setBusy(true); setMeldung("");
    try { setVorschau(await api("/belege/nicht-zugeordnet-verarbeiten?trockenlauf=1", "POST")); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const loseVerschieben = async () => {
    setBusy(true);
    try {
      const r = await api("/belege/nicht-zugeordnet-verarbeiten?trockenlauf=0", "POST");
      setMeldung(`${r.verschoben} Dateien einsortiert, ${r.liegen} liegen geblieben` +
                 (r.fehler ? `, ${r.fehler} Fehler.` : "."));
      setVorschau(null);
      await laden();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  // Das Status-Auswahlfeld zeigt den Ablauf eines Belegs plus die direkten Ablagen
  // Mahnungen/Sonstiges/Steuer. „Leistungsabrechnungen" ist ein Parallelgleis und fehlt.
  const bereiche = useMemo(
    () => daten?.bereich_ziele || daten?.bereich_ablauf
      || (daten?.bereiche || []).map((b) => ({ bereich: b.bereich, name: b.name })), [daten]);

  // Alle Belege, die als Gegenstück zu einer Abrechnungsposition in Frage kommen
  const zuordenbareBelege = useMemo(() => {
    const raus = [];
    (daten?.bereiche || []).forEach((b) => {
      if (b.bereich === "abrechnung") return;
      (b.buendel || []).forEach((bu) => (bu.belege || []).forEach((x) => {
        raus.push(x);
        (x.mahnungen || []).forEach((m) => raus.push(m));
      }));
    });
    return raus.sort((a, c) => String(c.datum_beleg || "").localeCompare(String(a.datum_beleg || "")));
  }, [daten]);

  const zeilenProps = { onStatus: statusWechseln, onGeaendert: laden, bereiche,
                        aktiv: sel.aktiv, markiert: sel.markiert, onWaehlen: waehlen,
                        onMeldung: setMeldung, onGeloescht: nachLoeschen, onAnsicht: ansichtGeaendert,
                        globalPasst, globalAktiv };

  return (
    <div style={gewaehltBeleg ? { ...S.page, maxWidth: "none" } : S.page}>
     <div style={S.layout}>
      <div style={S.layoutLinks}>
      <h1 style={S.h1}>Krankenversicherung</h1>
      <p style={S.sub}>
        Der Ordner in Dropbox bestimmt den Status. Änderst du hier den Status, wandert die Datei mit.
      </p>

      <GeminiWarnung wofuer="Neue Belege und Leistungsabrechnungen werden nicht ausgewertet. „Workflow anstoßen“ bucht nur Abrechnungen, die schon gelesen sind." />
      {fehler && <div style={S.err}>❌ {fehler}</div>}
      {meldung && <div style={S.info}>{meldung}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <button style={S.btn} onClick={scan} disabled={busy}>Ordner abgleichen</button>
        <button style={S.btn} onClick={offeneAuswerten} disabled={busy}>Offene auswerten (10)</button>
        <button style={S.btn} onClick={loseVorschau} disabled={busy}>Nicht zugeordnet einsortieren…</button>
        <button style={S.btnPrim} onClick={workflowPruefen} disabled={busy}
                title="Leistungsabrechnungen mit den Belegen abgleichen — erst Vorschau, dann Ausführen">
          {busy && !workflow ? "Prüfe…" : "Workflow anstoßen…"}
        </button>
        <button style={S.btn} onClick={dublettenPruefen} disabled={busy}
                title="Alle KRV-Ordner auf doppelte Belege vergleichen (Rechnungsnummer, Betrag, Datum)">
          {busy && !dubletten ? <><Kreisel />Vergleiche…</>
            : `Dubletten prüfen${daten?.dubletten_offen ? ` (${daten.dubletten_offen})` : ""}…`}
        </button>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem",
                        color: "var(--color-gray-600)", cursor: "pointer" }}>
          <input type="checkbox" checked={!mahnungenZeigen} onChange={mahnungenUmschalten} />
          Mahnungen ausblenden
        </label>
      </div>

      {/* Welle 104: Suche und Spaltenfilter über alle Ordner */}
      <div style={S.suchLeiste}>
        <input style={{ ...S.input, flex: "1 1 280px", width: "auto" }} value={suche}
               placeholder="Suche über alle Ordner und Spalten (Leerzeichen = UND)…"
               onChange={(e) => setSuche(e.target.value)} />
        <table style={{ borderCollapse: "collapse", width: "auto" }}>
          <thead><tr>
            {GLOBAL_SPALTEN.map((sp) => (
              <FilterKopf key={sp.key} label={sp.label} werte={gWerte[sp.key] || []} auswahl={gFilter[sp.key]}
                          onAendern={(neu) => setGFilter((f) => ({ ...f, [sp.key]: neu }))} />
            ))}
          </tr></thead>
        </table>
        {globalAktiv && (
          <span style={S.zaehler}>
            {treffer} von {alleZeilen.length} Belegen
            <button style={{ ...S.btnMini, marginLeft: 8 }}
                    onClick={() => { setSuche(""); setGFilter({}); }}>zurücksetzen</button>
          </span>
        )}
      </div>

      {workflow && (
        <WorkflowDialog bericht={workflow} busy={busy} onAusfuehren={workflowAusfuehren}
                        onClose={() => setWorkflow(null)} />
      )}

      {dubletten && (
        <DublettenDialog bericht={dubletten} auswahl={dublettenWahl} setAuswahl={setDublettenWahl}
                         busy={busy} ergebnis={dublettenErgebnis} fortschritt={dublettenFortschritt}
                         onLoeschen={() => dublettenLoeschen()} onKeine={dublettenKeine}
                         onZusammen={dublettenZusammen}
                         onLoeschenJetzt={(_behalten, ids) => dublettenLoeschen(ids)}
                         onClose={() => { setDubletten(null); setDublettenErgebnis(null); }} />
      )}

      {vorschau && (
        <div style={S.overlay} onClick={() => setVorschau(null)}>
          <div style={{ ...S.dialog, width: 820, maxHeight: "80vh", overflow: "auto" }}
               onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 4px" }}>Was würde wohin wandern?</h3>
            <p style={{ fontSize: "0.85rem", color: "var(--color-gray-600)", margin: "0 0 12px" }}>
              {vorschau.geprueft} geprüft · {vorschau.verschoben} würden verschoben ·{" "}
              {vorschau.liegen} bleiben liegen. Noch ist nichts passiert.
            </p>
            <table style={S.tabelle}>
              <thead><tr>
                <th style={S.th}>Datei</th><th style={S.th}>Art</th>
                <th style={S.th}>Ziel</th><th style={S.th}>Grund</th>
              </tr></thead>
              <tbody>
                {(vorschau.details || []).map((d) => (
                  <tr key={d.id}>
                    <td style={{ ...S.td, maxWidth: 320, wordBreak: "break-all" }}>{d.datei}</td>
                    <td style={S.td}>{d.art || "—"}</td>
                    <td style={S.td}>{d.ziel || <span style={{ color: "var(--color-gray-500)" }}>bleibt liegen</span>}</td>
                    <td style={{ ...S.td, fontSize: "0.78rem", color: "var(--color-gray-600)" }}>{d.grund}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button style={S.btn} onClick={() => setVorschau(null)} disabled={busy}>Abbrechen</button>
              <button style={S.btnPrim} onClick={loseVerschieben}
                      disabled={busy || !vorschau.verschoben}>
                {busy ? "Verschiebe…" : `${vorschau.verschoben} Dateien einsortieren`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sektion 1 — kommt später über die Web-Rekorder-Routine */}
      <div style={{ ...S.karte, ...S.gesperrt }}>
        <div style={S.kopf}>
          <span style={{ fontSize: "1rem" }}>▸</span>
          <h2 style={S.kopfTitel}>Belege hochladen</h2>
          <span style={S.zaehler}>
            Einreichen bei Signal Iduna — folgt als aufgezeichnete Routine (Web-Rekorder)
          </span>
        </div>
      </div>

      {!daten && !fehler && <div style={{ padding: 20 }}>Lade…</div>}
      {datenAnzeige?.bereiche
        ?.filter((b) => !["nicht_zugeordnet", "abrechnung"].includes(b.bereich))
        .filter((b) => !(b.bereich === "mahnung" && !mahnungenZeigen))
        .map((b) => (
          <Sektion key={b.bereich} daten={b} offenStart={b.bereich === "einzureichen"} {...zeilenProps} />
        ))}
      {datenAnzeige?.bereiche?.filter((b) => b.bereich === "abrechnung").map((b) => (
        <AbrechnungSektion key={b.bereich} daten={mitGlobal(b)} alleBelege={zuordenbareBelege}
                           aufklappen={globalAktiv && mitGlobal(b).buendel.length > 0}
                           onGeaendert={laden} onMeldung={setMeldung}
                           aktiv={sel.aktiv} markiert={sel.markiert} onWaehlen={waehlen} />
      ))}
      {datenAnzeige?.bereiche?.filter((b) => b.bereich === "nicht_zugeordnet").map((b) => (
        <Sektion key={b.bereich} daten={b} offenStart={false} {...zeilenProps} />
      ))}
      </div>

      {gewaehltBeleg && (
        <div style={S.layoutRechts}>
          <PdfVorschau key={gewaehltBeleg.id} beleg={gewaehltBeleg} onSchliessen={() => setVorschauZu(true)} />
        </div>
      )}
     </div>

      {dialog && (
        <StatusDialog belege={dialog.belege} ziel={dialog.ziel}
                      onClose={() => setDialog(null)}
                      onFertig={() => {
                        naechsteMerken(new Set(dialog.belege.map((b) => b.id)));
                        setDialog(null); laden();
                      }} />
      )}
    </div>
  );
}
